<template>
  <div
    v-intersect="{ handler: rearrange, options: { threshold: 0.9 } }"
    class="view-cover"
  >
    <v-lazy-image
      v-if="!crop"
      ref="image"
      v-resize="rearrange"
      :intersection-options="{root, rootMargin: '0px'}"
      :src="external ? src : getCdn(source)"
      @load="start()"
    />
    <image-display
      v-else
      :src="external ? src : getProxiedCdn(source)"
    />
    <transition v-if="!is2D" name="spots">
      <div v-show="loaded" ref="border" class="spot-border">
        <panorama-spot
          v-for="(spot, spotIdx) in spots"
          :id="spot.id"
          :key="spotIdx"
          :class="['panorama-spot', `panorama-spot-${spot.panorama_id}`]"
          :left="spot.position.left"
          :top="spot.position.top"
          :unit-type="unitType"
          :url="spot.url"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import VLazyImage from 'v-lazy-image';
import { cdnUrl, proxiedCDN } from '@/utils/urls';
import PanoramaSpot from './PanoramaSpot.vue';
import ImageDisplay from './ImageDisplay.vue';

export default {
  components: { VLazyImage, PanoramaSpot, ImageDisplay },
  props: {
    root: {
      type: HTMLElement,
      required: true,
    },
    crop: {
      type: Boolean,
      required: true,
    },
    src: {
      type: String,
      required: false,
    },
    type: {
      type: String,
      required: true,
    },
    spots: {
      type: Array,
      default: () => [],
    },
    unitType: {
      type: Object,
      required: true,
    },
    external: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loaded: false,
  }),
  computed: {
    is2D() {
      return this.type === '2D';
    },
    source() {
      return `${this.src}${this.is2D ? '?width=1200&height=9999' : '?width=1000&height=1000'}`;
    },
  },
  methods: {
    getCdn(source) {
      return cdnUrl(source);
    },
    getProxiedCdn(source) {
      return proxiedCDN(source);
    },
    start() {
      this.loaded = true;
      this.rearrange();
      this.$emit('load');
    },
    rearrange() {
      if (!this.loaded || !this.$refs.border) {
        return;
      }

      const {
        border,
        image: { $el: image },
      } = this.$refs;

      const {
        naturalWidth, naturalHeight, offsetWidth, offsetHeight,
      } = image;
      const naturalRatio = naturalWidth / naturalHeight;
      const offsetRatio = offsetWidth / offsetHeight;

      const [width, height] = (
        naturalRatio > offsetRatio
          ? [offsetWidth, offsetWidth / naturalRatio]
          : [offsetHeight * naturalRatio, offsetHeight]
      ).map(v => `${v}px`);

      Object.assign(border.style, { width, height });
    },
  },
};
</script>

<style lang="scss" scoped>
.view-cover {
  overflow: hidden;
  height: calc(100vh - 156px - var(--app-bar-height));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-items: center;
  padding: 30px;
  background: var(--unit-single-image-view-cover-background, #fff);

  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    flex: 1 1 auto;
  }

  .spot-border {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}

.spots-enter-active, .spots-leave-active {
  transition: opacity .5s;
}

.spots-enter, .spots-leave-to {
  opacity: 0;
}

</style>
